// Warp lineage view — FXC-8538 Wave 3.
//
// thread-web's first consumption of the shared @flexcompute/thread-graph
// component. A focus + expand lineage explorer over a project's
// provenance DAG (/nodes/dag), with clique/community clustering. Additive
// — it does NOT replace the rich React-Flow DAGView; it's a new surface
// that gives thread-web Warp's lineage/clustering capabilities. Route:
// /warp/:projectId.

import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { fetchLineage } from "../api/artifacts";
import { buildIndex, fromProvenanceGraph } from "@flexcompute/thread-graph";
import { LineageExplorer, type ClusterMode, type LineageSelection } from "@flexcompute/thread-graph/react";

const CLUSTER_MODES: ClusterMode[] = ["units", "communities", "cliques", "components", "none"];

export default function WarpLineagePage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [clusterBy, setClusterBy] = useState<ClusterMode>("units");
  const [selection, setSelection] = useState<LineageSelection | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["warp-lineage", projectId],
    queryFn: () => fetchLineage(projectId!, "main", { scope: "all", includePlots: true }),
    enabled: !!projectId,
  });

  const graph = useMemo(
    () => (data ? fromProvenanceGraph({ nodes: data.nodes, edges: data.edges }) : null),
    [data],
  );

  // Centre on the highest-degree node — a hub with rich lineage.
  const focusId = useMemo(() => {
    if (!graph || graph.nodes.length === 0) return "";
    const index = buildIndex(graph);
    let best = graph.nodes[0]!.id;
    let bestDeg = -1;
    for (const n of graph.nodes) {
      const deg =
        (index.predecessors.get(n.id)?.length ?? 0) + (index.successors.get(n.id)?.length ?? 0);
      if (deg > bestDeg) { bestDeg = deg; best = n.id; }
    }
    return best;
  }, [graph]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", background: "#0b1220", color: "#e2e8f0" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 16, padding: "10px 16px", borderBottom: "1px solid #1e293b", font: "13px ui-sans-serif, system-ui" }}>
        <strong>Warp lineage</strong>
        <span style={{ opacity: 0.55, fontSize: 12 }}>{projectId}</span>
        {graph ? <span style={{ opacity: 0.55, fontSize: 12 }}>· {graph.nodes.length} nodes · {graph.edges.length} edges</span> : null}
        <label style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
          cluster
          <select value={clusterBy} onChange={(e) => setClusterBy(e.target.value as ClusterMode)}
            style={{ background: "#111a2e", color: "#e2e8f0", border: "1px solid #1e293b", borderRadius: 4 }}>
            {CLUSTER_MODES.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        {selection ? <span style={{ fontSize: 12, opacity: 0.7 }}>{selection.node.type} · {selection.node.label}</span> : null}
      </header>
      <main style={{ flex: 1, position: "relative" }}>
        {isLoading ? <Centered>loading lineage…</Centered> : null}
        {error ? <Centered>failed to load lineage: {String(error)}</Centered> : null}
        {graph && focusId ? (
          <LineageExplorer
            graph={graph}
            focusId={focusId}
            renderer="force"
            clusterBy={clusterBy}
            onSelect={setSelection}
          />
        ) : graph ? (
          <Centered>no provenance nodes in this project/branch</Centered>
        ) : null}
      </main>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div style={{ height: "100%", display: "grid", placeItems: "center", opacity: 0.6, font: "13px ui-sans-serif" }}>{children}</div>;
}
